import { useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { SkeletonRows } from '@/components/ui/Skeleton'
import { cn } from '@/lib/cn'
import { formatKwacha, parseKwacha, toInputText } from '@/lib/money'
import { formatDay, formatMonth } from '@/lib/format'
import { formatZambianPhone } from '@/lib/phone'
import { lusakaToday } from '@/lib/periods'
import { listDepartmentMembers } from '@/data/members'
import type { DepartmentMember } from '@/data/members'
import { memberLedger, METHOD_LABELS, recordDuesPayment, reversePayment } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { listSheets } from '@/data/sheets'

// Sanity limits. The database allows far more; these catch a slipped decimal point.
const CONFIRM_FROM_NGWEE = 1_000_000 // K10,000: ask "is that right?"
const MAX_NGWEE = 100_000_000 // K1,000,000: refuse outright
const METHODS: PaymentMethod[] = ['cash', 'mobile_money', 'bank_transfer']

const newClientRef = () => crypto.randomUUID()

interface Done {
  paymentId: string
  memberId: string
  memberName: string
  amountNgwee: number
  method: PaymentMethod
  receivedOn: string
}

/**
 * Record a dues payment. THE most-used screen: pick the person, type the amount,
 * save. Target: under 15 seconds on a phone.
 */
export function PayPage({ deptId, memberId: presetMemberId }: { deptId: string; memberId?: string }) {
  const qc = useQueryClient()
  const today = lusakaToday()

  const membersQ = useQuery({ queryKey: ['members', deptId], queryFn: () => listDepartmentMembers(deptId) })
  const current = useMemo(() => (membersQ.data ?? []).filter((m) => m.leftOn === null), [membersQ.data])

  const [memberId, setMemberId] = useState<string | undefined>(presetMemberId)
  const [search, setSearch] = useState('')
  const [amountText, setAmountText] = useState('')
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [receivedOn, setReceivedOn] = useState(today)
  const [reference, setReference] = useState('')
  // null = not touched yet (the sheet dated the same day is suggested); '' = deliberately none.
  // Kept when recording the next payment, so a whole collection goes on the same sheet.
  const [sheetPick, setSheetPick] = useState<string | null>(null)
  const [bigOk, setBigOk] = useState(false)
  const [tried, setTried] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<Done | null>(null)
  const [undone, setUndone] = useState<string | null>(null)

  // Idempotency key, tied to the EXACT payment details. A retry of the same
  // payment reuses the key, so if the first tap reached the server but its reply
  // was lost, the retry returns the original instead of recording the money twice.
  // If ANY detail changes (amount, person, method, date, reference) the key changes
  // too: reusing the old key for different details would make the server hand back
  // the first payment and silently discard the correction. Cleared after a success
  // or an undo.
  const attempt = useRef<{ signature: string; key: string } | null>(null)
  const keyFor = (signature: string): string => {
    if (attempt.current?.signature !== signature) attempt.current = { signature, key: newClientRef() }
    return attempt.current.key
  }

  useEffect(() => setMemberId(presetMemberId), [presetMemberId])

  const member: DepartmentMember | undefined = current.find((m) => m.memberId === memberId)

  const ledgerQ = useQuery({
    queryKey: ['ledger', deptId, memberId],
    queryFn: () => memberLedger(deptId, memberId as string),
    enabled: Boolean(memberId),
  })
  const firstOpen = useMemo(
    () => [...(ledgerQ.data ?? [])].filter((c) => c.status === 'open').sort((a, b) => a.periodStart.localeCompare(b.periodStart))[0],
    [ledgerQ.data],
  )

  const amount = parseKwacha(amountText)
  const amountProblem =
    amountText.trim() === ''
      ? 'Enter the amount.'
      : amount === null
        ? 'Enter an amount like 50 or 50.00.'
        : amount <= 0
          ? 'The amount must be more than zero.'
          : amount > MAX_NGWEE
            ? 'That is larger than expected. Check the amount.'
            : null
  const dateProblem = !/^\d{4}-\d{2}-\d{2}$/.test(receivedOn)
    ? 'Choose the date.'
    : receivedOn > today
      ? 'The date cannot be in the future.'
      : receivedOn < '2020-01-01'
        ? 'That date is too long ago.'
        : null
  const needsBigConfirm = amount !== null && amount >= CONFIRM_FROM_NGWEE
  const bigProblem = needsBigConfirm && !bigOk ? 'Tick the box to confirm this large amount.' : null

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase()
    const digits = q.replace(/\D/g, '')
    const hits = current.filter(
      (m) => !q || m.fullName.toLowerCase().includes(q) || (digits.length >= 3 && (m.phone ?? '').replace(/\D/g, '').includes(digits)),
    )
    // With nothing typed, show people who owe money first: the usual reason to collect.
    return hits.sort((a, b) => (q ? 0 : b.arrearsNgwee - a.arrearsNgwee) || a.fullName.localeCompare(b.fullName)).slice(0, 8)
  }, [current, search])

  const sheetsQ = useQuery({ queryKey: ['sheets', deptId], queryFn: () => listSheets(deptId) })
  const sheets = sheetsQ.data ?? []
  const sheetId = method !== 'cash' ? '' : sheetPick ?? sheets.find((x) => x.sheetOn === receivedOn)?.id ?? ''

  const save = useMutation({
    mutationFn: (clientRef: string) =>
      recordDuesPayment({
        departmentId: deptId,
        memberId: memberId as string,
        amountNgwee: amount as number,
        method,
        receivedOn,
        reference: reference.trim() || null,
        sheetId: sheetId || null,
        clientRef,
      }),
    onSuccess: (paymentId) => {
      setDone({ paymentId, memberId: memberId as string, memberName: member?.fullName ?? 'the member', amountNgwee: amount as number, method, receivedOn })
      setError(null)
      attempt.current = null
      void qc.invalidateQueries({ queryKey: ['members', deptId] })
      void qc.invalidateQueries({ queryKey: ['ledger', deptId] })
      void qc.invalidateQueries({ queryKey: ['payments', deptId] })
      void qc.invalidateQueries({ queryKey: ['arrears', deptId] })
      void qc.invalidateQueries({ queryKey: ['sheets', deptId] })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not record the payment. Try again.'),
  })

  const undo = useMutation({
    mutationFn: () => reversePayment((done as Done).paymentId, 'Entered in error (undone straight after recording)'),
    onSuccess: () => {
      setUndone(`Undone: ${formatKwacha((done as Done).amountNgwee)} from ${(done as Done).memberName} has been cancelled.`)
      setDone(null)
      attempt.current = null
      void qc.invalidateQueries({ queryKey: ['members', deptId] })
      void qc.invalidateQueries({ queryKey: ['ledger', deptId] })
      void qc.invalidateQueries({ queryKey: ['payments', deptId] })
      void qc.invalidateQueries({ queryKey: ['sheets', deptId] })
      void qc.invalidateQueries({ queryKey: ['arrears', deptId] })
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not undo the payment.'),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (save.isPending) return
    setTried(true)
    setError(null)
    if (!memberId) return setError('Choose who paid.')
    if (amountProblem || dateProblem || bigProblem) return
    save.mutate(keyFor(JSON.stringify([deptId, memberId, amount, method, receivedOn, reference.trim(), sheetId])))
  }

  function recordAnother() {
    setDone(null)
    setUndone(null)
    setAmountText('')
    setReference('')
    setBigOk(false)
    setTried(false)
    setError(null)
    setSearch('')
    setMemberId(undefined)
  }

  // ------------------------------------------------------------ success screen
  if (done) {
    const after = current.find((m) => m.memberId === done.memberId)
    return (
      <div className="max-w-xl flex flex-col gap-5">
        <div role="status" className="rounded-2xl border border-ok/30 bg-ok-tint p-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="size-8 text-ok shrink-0" aria-hidden="true" />
            <div>
              <p className="text-section font-semibold text-ok">{formatKwacha(done.amountNgwee)} recorded</p>
              <p className="text-ok">
                from {done.memberName} · {METHOD_LABELS[done.method]} · {formatDay(done.receivedOn)}
              </p>
            </div>
          </div>
          <p className="mt-3 text-body text-ok">
            {after
              ? after.arrearsNgwee > 0
                ? `${after.fullName.split(' ')[0]} still owes ${formatKwacha(after.arrearsNgwee)}${after.oldestUnpaidPeriod ? ` (since ${formatMonth(after.oldestUnpaidPeriod)})` : ''}.`
                : `${after.fullName.split(' ')[0]} is up to date.`
              : ''}
          </p>
        </div>
        {error && (
          <p role="alert" className="text-body text-bad">
            {error}
          </p>
        )}
        <Button size="lg" onClick={recordAnother}>
          Record another payment
        </Button>
        <Link to="/d/$deptId/payments" params={{ deptId }} className="text-center text-brand hover:underline min-h-[44px] py-2">
          See all payments
        </Link>
        <div className="border-t border-line pt-4">
          <p className="text-body text-muted">Wrong person or wrong amount?</p>
          <Button variant="secondary" size="sm" className="mt-2" isLoading={undo.isPending} onClick={() => undo.mutate()}>
            Undo this payment
          </Button>
        </div>
      </div>
    )
  }

  // ------------------------------------------------------------ the form
  return (
    <form onSubmit={onSubmit} noValidate className="max-w-xl flex flex-col gap-6">
      <div>
        <h2 className="text-title font-semibold text-ink">Record a payment</h2>
        <p className="mt-1 text-muted">Dues collected from a member.</p>
      </div>

      {undone && (
        <p role="status" className="rounded-control border border-line bg-page p-3 text-body text-ink">
          {undone}
        </p>
      )}

      {/* 1. Who */}
      <section aria-labelledby="who-label" className="flex flex-col gap-3">
        <h3 id="who-label" className="font-medium text-ink">
          Who paid?
        </h3>
        {membersQ.isPending ? (
          <SkeletonRows rows={3} label="Loading members" />
        ) : membersQ.isError ? (
          <p role="alert" className="text-body text-bad">
            {membersQ.error instanceof Error ? membersQ.error.message : 'Could not load the members.'}
          </p>
        ) : member ? (
          <div className="flex items-center justify-between gap-3 rounded-card border-2 border-brand bg-brand-tint p-4">
            <div className="min-w-0">
              <p className="font-semibold text-ink truncate">{member.fullName}</p>
              <p className="text-body text-muted">
                {member.arrearsNgwee > 0 ? `Owes ${formatKwacha(member.arrearsNgwee)}` : 'Up to date'}
                {member.phone ? ` · ${formatZambianPhone(member.phone)}` : ''}
              </p>
            </div>
            <Button type="button" variant="secondary" size="sm" onClick={() => setMemberId(undefined)}>
              Change
            </Button>
          </div>
        ) : current.length === 0 ? (
          <p className="text-muted">
            There are no members yet.{' '}
            <Link to="/d/$deptId/members" params={{ deptId }} className="text-brand hover:underline">
              Add members first
            </Link>
            .
          </p>
        ) : (
          <>
            <Input
              label="Search by name or phone"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoComplete="off"
              autoFocus={!presetMemberId}
            />
            <ul className="flex flex-col gap-2" aria-label="Matching members">
              {matches.length === 0 && <li className="text-body text-muted">No one matches that.</li>}
              {matches.map((m) => (
                <li key={m.memberId}>
                  <button
                    type="button"
                    onClick={() => {
                      setMemberId(m.memberId)
                      setError(null)
                    }}
                    className="w-full text-left rounded-card border-2 border-line bg-surface px-4 py-3 min-h-[56px] hover:border-brand active:bg-page"
                  >
                    <span className="block font-medium text-ink">{m.fullName}</span>
                    <span className="block text-body text-muted">
                      {m.arrearsNgwee > 0 ? `Owes ${formatKwacha(m.arrearsNgwee)}` : 'Up to date'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>

      {/* 2. How much */}
      {member && (
        <>
          <section className="flex flex-col gap-3">
            <Input
              label="Amount (Kwacha)"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0.00"
              value={amountText}
              onChange={(e) => {
                setAmountText(e.target.value)
                setBigOk(false)
              }}
              error={tried ? (amountProblem ?? undefined) : undefined}
              autoFocus
            />
            {(member.arrearsNgwee > 0 || firstOpen) && (
              <div className="flex flex-wrap gap-2" aria-label="Quick amounts">
                {member.arrearsNgwee > 0 && (
                  <Button type="button" variant="secondary" size="sm" onClick={() => { setAmountText(toInputText(member.arrearsNgwee)); setBigOk(false) }}>
                    Everything owed ({formatKwacha(member.arrearsNgwee)})
                  </Button>
                )}
                {firstOpen && firstOpen.outstandingNgwee !== member.arrearsNgwee && (
                  <Button type="button" variant="secondary" size="sm" onClick={() => { setAmountText(toInputText(firstOpen.outstandingNgwee)); setBigOk(false) }}>
                    One month ({formatKwacha(firstOpen.outstandingNgwee)})
                  </Button>
                )}
              </div>
            )}
            {needsBigConfirm && (
              <label className="flex items-start gap-3 rounded-control border border-wait/40 bg-wait-tint p-3 text-body text-wait min-h-[44px]">
                <input type="checkbox" className="mt-0.5 size-5" checked={bigOk} onChange={(e) => setBigOk(e.target.checked)} />
                <span>
                  {formatKwacha(amount as number)} is a large payment. I have checked it is correct.
                  {tried && bigProblem && <span className="block text-bad mt-1">{bigProblem}</span>}
                </span>
              </label>
            )}
          </section>

          {/* 3. How */}
          <fieldset className="flex flex-col gap-2">
            <legend className="font-medium text-ink mb-1">How did they pay?</legend>
            <div role="radiogroup" aria-label="Payment method" className="grid grid-cols-3 gap-2">
              {METHODS.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="radio"
                  aria-checked={method === m}
                  onClick={() => setMethod(m)}
                  className={cn(
                    'rounded-card border-2 px-2 py-3 min-h-[56px] text-body font-medium transition-colors',
                    method === m ? 'border-brand bg-brand-tint text-brand-ink' : 'border-line bg-surface text-neutral',
                  )}
                >
                  {METHOD_LABELS[m]}
                </button>
              ))}
            </div>
          </fieldset>

          <Input
            label="Date received"
            type="date"
            max={today}
            value={receivedOn}
            onChange={(e) => setReceivedOn(e.target.value)}
            error={tried ? (dateProblem ?? undefined) : undefined}
          />

          {method === 'cash' && sheets.length > 0 && (
            <Select
              label="Collection sheet (optional)"
              value={sheetId}
              onChange={(e) => setSheetPick(e.target.value)}
              options={[{ value: '', label: 'Not on a sheet' }, ...sheets.slice(0, 30).map((x) => ({ value: x.id, label: `${formatDay(x.sheetOn)}${x.label ? ` · ${x.label}` : ''}` }))]}
              hint="The sheet's photo is the proof for every payment written on it."
            />
          )}

          {method !== 'cash' && (
            <Input
              label={method === 'mobile_money' ? 'Transaction ID (optional)' : 'Bank reference (optional)'}
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              autoComplete="off"
            />
          )}

          {error && (
            <p role="alert" className="rounded-control border border-bad/30 bg-bad-tint p-3 text-body text-bad">
              {error}
            </p>
          )}

          <Button type="submit" size="lg" isLoading={save.isPending} className="w-full">
            {save.isPending ? 'Saving…' : amount !== null && amount > 0 ? `Record ${formatKwacha(amount)} from ${member.fullName.split(' ')[0]}` : 'Record payment'}
          </Button>
        </>
      )}
      {!member && error && (
        <p role="alert" className="text-body text-bad">
          {error}
        </p>
      )}
    </form>
  )
}
